import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { List, MapPin } from "lucide-react";
import { z } from "zod";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/features/auth/store";
import {
  MasterPage,
  type MasterSearch,
} from "@/features/masters/components/master-page";
import { PersonnelCoveragePage } from "@/features/masters/components/personnel-coverage-page";
import { MASTERS } from "@/features/masters/registry";

const spec = MASTERS["production-personnel"];

const search = z.object({
  // By State first (user, 2026-10-23); People is the generic master list.
  view: z.enum(["state", "people"]).default("state"),
  // The State the "Who gets mailed" panel shows (or "__none").
  state: z.string().max(80).optional(),
  // "Pick a person" lookup (user, 2026-10-23): their row id, and the locations ticked (unset: all).
  person: z.number().int().optional(),
  locs: z.array(z.string().max(80)).max(40).optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(200).default(50),
  q: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(["asc", "desc"]).default("asc"),
  active: z.enum(["true", "false", "all"]).default("true"),
  filter: z.string().max(60).optional(),
});

/** Masters ▸ Production Personnel: its own page ahead of /masters/$key, for the By State view. */
export const Route = createFileRoute("/_app/masters/production-personnel")({
  validateSearch: search,
  beforeLoad: () => {
    if (!useAuth.getState().can(spec.permission.view))
      throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const { view, state, person, locs, ...s } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // The generic list's own keys (MasterSearch) plus this page's view, State, person and locations.
  const go = (
    next: Partial<MasterSearch> & {
      view?: "state" | "people";
      state?: string | undefined;
      person?: number | undefined;
      locs?: string[] | undefined;
    }
  ) => void navigate({ search: (prev) => ({ ...prev, ...next }) });
  const toggle = (
    <ToggleGroup
      type="single"
      value={view}
      onValueChange={(v) => v && go({ view: v as "state" | "people", page: 1 })}
      aria-label="View"
    >
      <ToggleGroupItem value="state">
        <MapPin className="size-3.5" /> By State
      </ToggleGroupItem>
      <ToggleGroupItem value="people">
        <List className="size-3.5" /> People
      </ToggleGroupItem>
    </ToggleGroup>
  );
  return view === "state" ? (
    <PersonnelCoveragePage
      state={state}
      person={person}
      locs={locs}
      viewToggle={toggle}
      onSearch={go}
    />
  ) : (
    <MasterPage spec={spec} search={s} onSearch={go} toolbarStart={toggle} />
  );
}
